import { KEY_CODE_BUSAN } from '@/constants/app';
import type { LocalizedString } from '@/services/dto';
import { useConfigStore } from '@/stores';
import { AppUtils } from '@/utils/app';
import { getFloorDisplayLabel } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

type Props = {
    tabIndex?: number;
    selectedFloorName?: LocalizedString;
    isShowAllFloorGuide?: boolean;
    buildingName?: string;
    isMultiBuilding?: boolean;
};

export const HorizontalMapHeader = ({
    tabIndex,
    selectedFloorName,
    isShowAllFloorGuide = false,
    buildingName,
    isMultiBuilding = false,
}: Props) => {
    const { t, i18n } = useTranslation();
    const storeCode = useConfigStore((state) => state.config?.server?.store?.storeCode) ?? '';
    const isBusan = KEY_CODE_BUSAN.includes(storeCode);

    const floorDisplayText = useMemo(() => {
        if (isShowAllFloorGuide) {
            return t('floor_guide.label.all_floors_guide');
        }
        const floorName = selectedFloorName?.[i18n.language] ?? selectedFloorName?.ko ?? '';
        return getFloorDisplayLabel(floorName);
    }, [selectedFloorName, i18n.language, isShowAllFloorGuide, t]);

    const floorAriaLabel = useMemo(() => {
        if (isShowAllFloorGuide) {
            return t('floor_guide.label.all_floors_guide');
        }
        const floorName = selectedFloorName?.[i18n.language] ?? selectedFloorName?.ko ?? '';
        return t('floor_guide.text_to_speech.floor', {
            floorName: AppUtils.getFloorTTSText(floorName),
        });
    }, [selectedFloorName, i18n.language, isShowAllFloorGuide, t]);

    return (
        <section className={styles['map-header']} data-region-nav data-region-order={1}>
            <div className={styles['map-header__title-group']}>
                {isMultiBuilding && buildingName ? (
                    <p className={styles['map-header__building']} data-region-order={1}>
                        {buildingName}
                    </p>
                ) : null}
                {floorDisplayText ? (
                    <p
                        tabIndex={tabIndex}
                        data-interactive
                        data-region-order={isMultiBuilding ? 2 : 1}
                        aria-label={floorAriaLabel}
                        className={clsx(styles['map-header__floor'], 'focus-border number')}
                    >
                        {floorDisplayText}
                        {isBusan && floorDisplayText === 'G' && (
                            <span className={styles['map-header__ground']}>(ground)</span>
                        )}
                    </p>
                ) : null}
            </div>
        </section>
    );
};
